import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Wallet, 
  TrendingUp, 
  TrendingDown,
  BarChart3, 
  ArrowRight, 
  Zap, 
  Repeat, 
  Layers, 
  Clock, 
  DollarSign, 
  ChevronRight, 
  Globe, 
  Star, 
  ArrowUpRight, 
  ArrowDownRight,
  ArrowDownLeft,
  Bitcoin,
  LineChart,
  PieChart,
  Activity,
  Newspaper,
  ExternalLink,
  ChevronLeft,
  Package
} from 'lucide-react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { MarketData, NewsItem, PortfolioSnapshot, DatabaseUserAsset } from '../hooks/useDatabase';
import { FuturesPosition, Transaction, TradingMode } from '../App';
import { useDatabase } from '../hooks/useDatabase';
import { useMarketData } from '../contexts/MarketDataContext';
import { useBybitData } from '../contexts/BybitDataContext';
import GiveawayCampaignPopup from '../components/GiveawayCampaignPopup';
import GiveawayWinnerPopup from '../components/GiveawayWinnerPopup';
import GiveawayComingSoonPopup from '../components/GiveawayComingSoonPopup';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface HomePageProps {
  usdtBalance: number;
  btcBalance: number;
  futuresPositions: FuturesPosition[];
  transactions: Transaction[];
  setTradingMode: (mode: TradingMode) => void;
  newsItems: NewsItem[];
  totalPortfolioValue?: number;
  userAssets?: DatabaseUserAsset[];
}

const HomePage: React.FC<HomePageProps> = ({
  usdtBalance,
  btcBalance,
  futuresPositions,
  transactions,
  setTradingMode,
  newsItems,
  totalPortfolioValue = 0,
  userAssets = []
}) => {
  const { t } = useTranslation();
  const { fetchPortfolioSnapshots, portfolioSnapshots, createPortfolioSnapshot, coingeckoMarketCapData } = useDatabase();
  const { marketData, isConnected: isRealtimeConnected, getPriceBySymbol } = useMarketData();
  const { getPriceBySymbol: getBybitPrice } = useBybitData();
  const [activeTab, setActiveTab] = useState<'overview' | 'positions' | 'transactions'>('overview');
  const [filteredSnapshots, setFilteredSnapshots] = useState<PortfolioSnapshot[]>([]);
  const [showCampaignPopup, setShowCampaignPopup] = useState(false);
  const [showWinnerPopup, setShowWinnerPopup] = useState(false);
  const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);

  // Helper function to format date safely
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString();
    } catch (error) {
      return 'N/A';
    }
  };
  
  // News pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  
  // Chart period state
  const [chartPeriod, setChartPeriod] = useState<'1W' | '1M' | '3M' | '1Y' | 'All'>('1M');
  
  const currentBtcPrice = getBybitPrice('BTCUSDT') || getPriceBySymbol('BTCUSDT') || 0;
  
  // Calculate actual portfolio value if not provided or is zero
  const actualPortfolioValue = totalPortfolioValue > 0 ? totalPortfolioValue : (usdtBalance + (btcBalance * currentBtcPrice));
  
  // Check if data is still loading
  const isDataLoading = actualPortfolioValue === 0 && (usdtBalance === 0 && btcBalance === 0 && currentBtcPrice === 0);
  
  // Calculate total PnL from futures positions
  const totalPositionsPnl = futuresPositions.reduce((sum, position) => {
    // Calculate correct unrealized PnL without extra leverage multiplication
    const entryPrice = position.entryPrice || position.entry_price || 0;
    
    // Get live price from market data context first, then fallback to position data
    let currentPrice = 0;
    const livePrice = getPriceBySymbol(position.symbol);
    if (livePrice > 0) {
      currentPrice = livePrice;
    } else {
      currentPrice = position.currentPrice || position.current_price || 0;
    }
    
    const amount = position.amount || 0;
    const side = position.side || 'long';
    let unrealizedPnl = 0;
    if (position.side === 'long') {
      unrealizedPnl = (currentPrice - entryPrice) * amount;
    } else {
      unrealizedPnl = (entryPrice - currentPrice) * amount;
    }
    
    return sum + unrealizedPnl;
  }, 0);

  // Fetch portfolio snapshots based on selected period
  useEffect(() => {
    const fetchSnapshotsForPeriod = async () => {
      let days = 30; // Default to 1 month
      
      switch (chartPeriod) {
        case '1W':
          days = 7;
          break;
        case '1M':
          days = 30;
          break;
        case '3M':
          days = 90;
          break;
        case '1Y':
          days = 365;
          break;
        case 'All':
          days = 9999; // Fetch all data
          break;
      }
      
      await fetchPortfolioSnapshots(days);
    };
    
    fetchSnapshotsForPeriod();
  }, [chartPeriod, fetchPortfolioSnapshots]);

  // Filter and format portfolio snapshots for the chart
  useEffect(() => {
    if (portfolioSnapshots.length === 0) {
      // If no snapshots, create a default one with current values
      const today = new Date();
      const defaultSnapshots = [{
        id: 'default',
        user_id: '',
        snapshot_date: today.toISOString().split('T')[0],
        total_value: totalPortfolioValue,
        usdt_balance: usdtBalance,
        btc_balance: btcBalance,
        btc_price: currentBtcPrice,
        created_at: today.toISOString()
      }];
      
      setFilteredSnapshots(defaultSnapshots);
      return;
    }
    
    // Filter snapshots based on selected period
    let filteredData = [...portfolioSnapshots];
    
    // Sort by date
    filteredData.sort((a, b) => 
      new Date(a.snapshot_date).getTime() - new Date(b.snapshot_date).getTime()
    );
    
    setFilteredSnapshots(filteredData);
  }, [portfolioSnapshots, totalPortfolioValue, usdtBalance, btcBalance, currentBtcPrice]);
  
  // Get top performing assets
  const getTopPerformingAssets = () => {
    return marketData
      .filter(data => data.price > 0 && data.price !== 104325.3716)
      .sort((a, b) => (b.change_24h ?? 0) - (a.change_24h ?? 0))
      .slice(0, 5);
  };
  
  // Get recent transactions
  const getRecentTransactions = () => {
    return transactions.slice(0, 5);
  };

  // Pagination logic for news
  const indexOfLastNews = currentPage * itemsPerPage;
  const indexOfFirstNews = indexOfLastNews - itemsPerPage;
  const currentNews = newsItems.slice(indexOfFirstNews, indexOfLastNews);
  const totalPages = Math.ceil(newsItems.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  // Calculate high, low, and change values for the selected period
  const calculateStats = () => {
    if (filteredSnapshots.length === 0) {
      return {
        high: actualPortfolioValue,
        low: actualPortfolioValue,
        change: 0,
        changePercentage: 0
      };
    }
    
    const values = filteredSnapshots.map(snapshot => snapshot.total_value);
    const high = Math.max(...values);
    const low = Math.min(...values);
    
    // Calculate change from first to last snapshot
    const firstValue = filteredSnapshots[0].total_value;
    const lastValue = filteredSnapshots[filteredSnapshots.length - 1].total_value;
    const change = lastValue - firstValue;
    const changePercentage = firstValue > 0 ? (change / firstValue) * 100 : 0;
    
    return { high, low, change, changePercentage };
  };
  
  const { high, low, change, changePercentage } = calculateStats();

  // Prepare chart data
  const chartData = {
    labels: filteredSnapshots.map(snapshot => {
      const date = new Date(snapshot.snapshot_date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: 'Portfolio Value',
        data: filteredSnapshots.map(snapshot => snapshot.total_value),
        borderColor: 'rgba(59, 130, 246, 1)', // blue-500
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.4,
        fill: true
      }
    ]
  };
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(15, 23, 42, 0.9)',
        titleColor: 'rgba(255, 255, 255, 1)',
        bodyColor: 'rgba(148, 163, 184, 1)',
        borderColor: 'rgba(51, 65, 85, 0.5)',
        borderWidth: 1,
        padding: 10,
        displayColors: false,
        callbacks: {
          label: function(context: any) {
            return `$${context.raw.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: 'rgba(148, 163, 184, 1)',
          maxTicksLimit: 7
        }
      },
      y: {
        grid: {
          color: 'rgba(51, 65, 85, 0.1)'
        },
        ticks: {
          color: 'rgba(148, 163, 184, 1)',
          callback: function(value: any) {
            return `$${value.toLocaleString('en-US', { notation: 'compact', compactDisplay: 'short' })}`;
          }
        }
      }
    }
  };

  // Get period label for stats
  const getPeriodLabel = () => {
    switch (chartPeriod) {
      case '1W': return '1W';
      case '1M': return '1M';
      case '3M': return '3M';
      case '1Y': return '1Y';
      case 'All': return 'All Time';
      default: return '';
    }
  };

  // Sort market data by market cap for Market Overview section
  const getSortedMarketData = () => {
    // Create a map of market caps from coingeckoMarketCapData
    const marketCapMap = new Map();
    
    if (coingeckoMarketCapData && coingeckoMarketCapData.length > 0) {
      coingeckoMarketCapData.forEach(coin => {
        marketCapMap.set(coin.symbol.toUpperCase(), coin.market_cap);
      });
    }
    
    // Filter valid market data
    const validMarketData = marketData.filter(data => 
      data.price > 0 && 
      data.price !== 104325.3716 && // Filter out placeholder price
      data.symbol.endsWith('USDT') // Only include USDT pairs
    );
    
    // Sort by market cap (descending)
    return validMarketData.sort((a, b) => {
      // Extract base symbol (remove USDT)
      const aSymbol = a.symbol.replace('USDT', '').toLowerCase();
      const bSymbol = b.symbol.replace('USDT', '').toLowerCase();
      
      // Get market caps from map
      const aMarketCap = marketCapMap.get(aSymbol.toUpperCase()) || 0;
      const bMarketCap = marketCapMap.get(bSymbol.toUpperCase()) || 0;
      
      // Sort by market cap (descending)
      return bMarketCap - aMarketCap;
    }).slice(0, 5); // Take top 5 for display
  };

  return (
    <div className="p-8">
      {/* Giveaway Popups */}
      <GiveawayCampaignPopup forceShow={showCampaignPopup} />
      <GiveawayWinnerPopup forceShow={showWinnerPopup} />
      <GiveawayComingSoonPopup forceShow={showComingSoonPopup} />

      {/* Welcome Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
            {t('home.welcomeTitle')}
          </h1>
        </div>
        <p className="text-slate-400">
          {t('home.welcomeSubtitle')}
        </p>
      </div>
      
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Wallet size={20} className="text-white" />
            </div>
            <div>
              <div className="text-slate-400">{getPeriodLabel()} {t('home.change')}</div>
              <div className="text-2xl font-bold text-white" translate="no">
                {isDataLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-lg">Loading...</span>
                  </div>
                ) : (
                  `$${actualPortfolioValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center mt-4 text-sm">
            <div className="text-slate-400">{getPeriodLabel()} Change</div>
            <div className={changePercentage >= 0 ? "text-emerald-400" : "text-red-400"} translate="no">
              {isDataLoading ? (
                <span className="text-slate-500">--</span>
              ) : (
                `${changePercentage >= 0 ? '+' : ''}${changePercentage.toFixed(2)}%`
              )}
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/25">
              <TrendingUp size={20} className="text-white" />
            </div>
            <div>
              <div className="text-slate-400 text-sm">Positions P&L</div>
              <div className={`text-2xl font-bold ${totalPositionsPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`} translate="no">
                {totalPositionsPnl >= 0 ? '+' : ''}{totalPositionsPnl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center mt-4 text-sm">
            <div className="text-slate-400">Open Positions</div>
            <div className="text-white" translate="no">{futuresPositions.length}</div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/25">
              <Bitcoin size={20} className="text-white" />
            </div>
            <div>
              <div className="text-slate-400 text-sm">BTC Price</div>
              <div className="text-2xl font-bold text-white" translate="no">${currentBtcPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
          </div>
          <div className="flex justify-between items-center mt-4 text-sm">
            <div className="text-slate-400">24h Change</div>
            <div className={`${(marketData.find(d => d.symbol === 'BTCUSDT')?.change_24h ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} translate="no">
              {(marketData.find(d => d.symbol === 'BTCUSDT')?.change_24h ?? 0) >= 0 ? '+' : ''}
              {(marketData.find(d => d.symbol === 'BTCUSDT')?.change_24h ?? 0).toFixed(2)}%
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/25">
              <BarChart3 size={20} className="text-white" />
            </div>
            <div>
              <div className="text-slate-400 text-sm">Trading Volume</div>
              <div className="text-2xl font-bold text-white" translate="no">$125,430.50</div>
            </div>
          </div>
          <div className="flex justify-between items-center mt-4 text-sm">
            <div className="text-slate-400">{getPeriodLabel()} Change</div>
            <div className="text-emerald-400" translate="no">+12.5%</div>
          </div>
        </div>
      </div>

      {/* Portfolio Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white">Portfolio Performance</h2>
            <div className="flex gap-2">
              {(['1W', '1M', '3M', '1Y', 'All'] as const).map((period) => (
                <button
                  key={period}
                  onClick={() => setChartPeriod(period)}
                  className={`px-3 py-1 rounded-lg text-sm transition-all ${
                    chartPeriod === period
                      ? 'bg-blue-500 text-white'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
          
          <div className="h-64 mb-4">
            <Line data={chartData} options={chartOptions} />
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-slate-400">High ({getPeriodLabel()})</div>
              <div className="text-white font-semibold" translate="no">${high.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div>
              <div className="text-slate-400">Low ({getPeriodLabel()})</div>
              <div className="text-white font-semibold" translate="no">${low.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            <div>
              <div className="text-slate-400">Change ({getPeriodLabel()})</div>
              <div className={`font-semibold ${change >= 0 ? 'text-emerald-400' : 'text-red-400'}`} translate="no">
                {change >= 0 ? '+' : ''}${change.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>

        {/* Market Overview */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
          <h3 className="text-lg font-bold text-white mb-4">Market Overview</h3>
          <div className="space-y-3">
            {getSortedMarketData().map((data) => (
              <div key={data.symbol} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">
                      {data.symbol.replace('USDT', '').slice(0, 2)}
                    </span>
                  </div>
                  <div>
                    <div className="text-white font-medium">{data.symbol.replace('USDT', '')}</div>
                    <div className="text-slate-400 text-sm" translate="no">${data.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>
                </div>
                <div className={`text-sm font-medium ${(data.change_24h ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} translate="no">
                  {(data.change_24h ?? 0) >= 0 ? '+' : ''}{(data.change_24h ?? 0).toFixed(2)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Tabs */}
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 shadow-2xl">
        <div className="flex border-b border-slate-700/50">
          {(['overview', 'positions', 'transactions'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-4 font-medium capitalize transition-all ${
                activeTab === tab
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-500/10'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Quick Actions */}
              <div>
                <h3 className="text-lg font-bold text-white mb-4">Quick Actions</h3>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setTradingMode('spot')}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white p-4 rounded-xl transition-all duration-200 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-105"
                  >
                    <div className="flex items-center gap-3">
                      <Repeat size={20} />
                      <div className="text-left">
                        <div className="font-semibold">Spot Trading</div>
                        <div className="text-sm opacity-80">Buy & Sell Crypto</div>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setTradingMode('futures')}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white p-4 rounded-xl transition-all duration-200 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-105"
                  >
                    <div className="flex items-center gap-3">
                      <TrendingUp size={20} />
                      <div className="text-left">
                        <div className="font-semibold">Futures</div>
                        <div className="text-sm opacity-80">Leverage Trading</div>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setTradingMode('binary')}
                    className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white p-4 rounded-xl transition-all duration-200 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 hover:scale-105"
                  >
                    <div className="flex items-center gap-3">
                      <Activity size={20} />
                      <div className="text-left">
                        <div className="font-semibold">Binary Options</div>
                        <div className="text-sm opacity-80">Quick Predictions</div>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setTradingMode('wallet')}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white p-4 rounded-xl transition-all duration-200 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 hover:scale-105"
                  >
                    <div className="flex items-center gap-3">
                      <Wallet size={20} />
                      <div className="text-left">
                        <div className="font-semibold">Wallet</div>
                        <div className="text-sm opacity-80">Manage Funds</div>
                      </div>
                    </div>
                  </button>
                </div>
                
                <div className="text-center mt-6">
                  <p className="text-slate-400 text-sm mb-4">
                    {t('home.swapDescription')}
                  </p>
                  <button
                    onClick={() => setTradingMode('swap')}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/25 flex items-center gap-2 mx-auto"
                  >
                    <ArrowRight size={18} />
                    {t('home.tradeNow')}
                  </button>
                </div>
              </div>
              
              {/* Recent Transactions */}
              <div>
                <h3 className="text-lg font-bold text-white mb-4">{t('home.recentTransactions')}</h3>
                <div className="space-y-3">
                  {getRecentTransactions().length > 0 ? (
                    getRecentTransactions().map((transaction, index) => (
                      <div key={index} className="flex items-center justify-between bg-slate-900/40 rounded-xl p-4 border border-slate-700/40">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            transaction.amount > 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'
                          }`}>
                            {transaction.amount > 0 ? (
                              <ArrowDownRight size={16} className="text-emerald-400" />
                            ) : (
                              <ArrowUpRight size={16} className="text-red-400" />
                            )}
                          </div>
                          <div>
                            <div className="text-white font-medium">{transaction.description}</div>
                            <div className="text-slate-400 text-sm">
                              {formatDate(transaction.created_at || transaction.timestamp)}
                            </div>
                          </div>
                        </div>
                        <div className={`font-bold ${transaction.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {transaction.amount > 0 ? '+' : ''}{transaction.amount.toFixed(2)} USDT
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Package size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="mb-2">{t('home.noTransactions')}</p>
                      <button
                        onClick={() => setTradingMode('wallet')}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        {t('home.goToWallet')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'positions' && (
            <div className="space-y-4">
              {futuresPositions.length > 0 ? (
                futuresPositions.map((position, index) => {
                  // Get live price from market data context first, then fallback to position data
                  let currentPrice = 0;
                  const livePrice = getPriceBySymbol(position.symbol);
                  if (livePrice > 0) {
                    currentPrice = livePrice;
                  } else {
                    currentPrice = position.currentPrice || position.current_price || 0;
                  }
                  
                  const entryPrice = position.entryPrice || position.entry_price || 0;
                  const amount = position.amount || 0;
                  const side = position.side || 'long';
                  
                  // Calculate unrealized PnL based on live price (WITHOUT leverage multiplier)
                  let unrealizedPnl = 0;
                  if (side === 'long') {
                    unrealizedPnl = (currentPrice - entryPrice) * amount;
                  } else {
                    unrealizedPnl = (entryPrice - currentPrice) * amount;
                  }
                  
                  return (
                    <div key={position.id || index} className="flex items-center justify-between bg-slate-900/40 rounded-xl p-4 border border-slate-700/40">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          side === 'long' ? 'bg-emerald-500/20' : 'bg-red-500/20'
                        }`}>
                          {side === 'long' ? (
                            <TrendingUp size={16} className="text-emerald-400" />
                          ) : (
                            <TrendingDown size={16} className="text-red-400" />
                          )}
                        </div>
                        <div>
                          <div className="text-white font-medium">{position.symbol}</div>
                          <div className="text-slate-400 text-sm">
                            {side.toUpperCase()} • {position.leverage || 1}x
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`font-bold ${unrealizedPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {unrealizedPnl >= 0 ? '+' : ''}{unrealizedPnl.toFixed(2)} USDT
                        </div>
                        <div className="text-slate-400 text-sm">
                          ${entryPrice.toFixed(2)} → ${currentPrice.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Package size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="mb-2">{t('home.noPositions')}</p>
                  <button
                    onClick={() => setTradingMode('futures')}
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {t('home.startTrading')}
                  </button>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'transactions' && (
            <div className="space-y-4">
              {getRecentTransactions().length > 0 ? (
                getRecentTransactions().map((transaction, index) => (
                  <div key={index} className="flex items-center justify-between bg-slate-900/40 rounded-xl p-4 border border-slate-700/40">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        transaction.amount > 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'
                      }`}>
                        {transaction.amount > 0 ? (
                          <ArrowDownRight size={16} className="text-emerald-400" />
                        ) : (
                          <ArrowUpRight size={16} className="text-red-400" />
                        )}
                      </div>
                      <div>
                        <div className="text-white font-medium">{transaction.description}</div>
                        <div className="text-slate-400 text-sm">
                          {formatDate(transaction.created_at || transaction.timestamp)}
                        </div>
                      </div>
                    </div>
                    <div className={`font-bold ${transaction.amount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {transaction.amount > 0 ? '+' : ''}{transaction.amount.toFixed(2)} USDT
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Package size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="mb-2">{t('home.noTransactions')}</p>
                  <button
                    onClick={() => setTradingMode('wallet')}
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {t('home.goToWallet')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* News Section */}
      <div className="mt-8 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center shadow-lg shadow-indigo-500/25">
              <Newspaper size={20} className="text-white" />
            </div>
            <h2 className="text-xl font-semibold text-white">{t('home.marketNews')}</h2>
          </div>
          <div className="text-xs text-slate-400 bg-slate-700/50 px-3 py-1 rounded-full">
            {newsItems.length} articles
          </div>
        </div>
        
        <div className="space-y-6">
          {currentNews.length > 0 ? (
            currentNews.map((news, index) => (
              <div key={news.id || index} className="bg-slate-900/40 rounded-xl p-6 border border-slate-700/40 hover:border-slate-600/50 transition-all duration-300">
                <div className="flex gap-6">
                  {news.image_url && (
                    <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={news.image_url} 
                        alt={news.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`text-xs px-3 py-1 rounded-full font-medium uppercase tracking-wide ${
                        news.category === 'crypto' ? 'bg-orange-500/20 text-orange-400' :
                        news.category === 'markets' ? 'bg-blue-500/20 text-blue-400' :
                        news.category === 'economy' ? 'bg-green-500/20 text-green-400' :
                        news.category === 'technology' ? 'bg-purple-500/20 text-purple-400' :
                        'bg-indigo-500/20 text-indigo-400'
                      }`}>
                        {news.category}
                      </span>
                      <span className="text-xs text-slate-500">{news.source}</span>
                      <span className="text-xs text-slate-500">•</span>
                      <span className="text-xs text-slate-500">
                        {new Date(news.published_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="font-semibold text-white mb-3 text-lg leading-relaxed">{news.title}</h3>
                    <p className="text-slate-400 text-sm line-clamp-3 mb-4 leading-relaxed">{news.summary}</p>
                    <a 
                      href={news.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                    >
                      {t('home.readMore')}
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Newspaper size={64} className="mx-auto mb-6 opacity-50" />
              <p className="text-lg mb-2">{t('home.noNewsAvailable')}</p>
              <p className="text-sm text-slate-600">{t('home.checkBackLater')}</p>
            </div>
          )}
        </div>
        
        {/* News Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-700/50">
            <div className="text-sm text-slate-400">
              {t('home.pageInfo', { current: currentPage, total: totalPages })}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className="p-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-slate-300 px-4">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;